import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { languages } from "simplydo/core/i18n";
import {
  DEFAULT_ORG_TIMEZONE,
  getTimezoneOptions,
} from "simplydo/core/timezones";
import { styled } from "simplydo/react/styled";
import {
  Button,
  Checkbox,
  Divider,
  Header,
  Icon,
  Message,
  Segment,
  Select,
  type SemanticICONS,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import SelectableDropdown from "components/lib/SelectableDropdown";

import type {
  OnOrganisationEditedFn,
  OrganisationSettingsUpdate,
  SettingsOwnerOrganisation,
  SubscriptionMethod,
} from "../../types";
import IpRestriction from "./IpRestriction";
import PointOfContact from "./PointOfContact";

export const SegmentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  gap: 12px;
  margin-bottom: 12px;

  .ui.segment {
    margin: 0;
    flex: 1 0 40%;
  }
`;

const possibleMethods: { key: "email" | "push"; icon: SemanticICONS }[] = [
  { key: "email", icon: "mail" },
  { key: "push", icon: "mobile" },
];

type SubscriptionItem = (typeof constants.subscriptions)[number] & {
  unsupportedMethods?: string[];
};

type DefaultSubscriptionsMap = Record<string, SubscriptionMethod[]>;

type SubscriptionProps = {
  sub: SubscriptionItem;
  hasSub: (key: string, method: SubscriptionMethod) => boolean;
  toggleSub: (
    key: string,
    method: SubscriptionMethod,
    checked?: boolean,
  ) => void;
};

function Subscription({ sub, hasSub, toggleSub }: SubscriptionProps) {
  const { t } = useTranslation();

  return (
    <div
      key={sub.key}
      style={{
        marginTop: 5,
        marginBottom: 5,
        paddingTop: 3,
        paddingBottom: 3,
        borderBottom: "1px solid #e9ebee",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {t(sub.text)}
      <div
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        {possibleMethods.map((m) => (
          <Checkbox
            disabled={(sub.unsupportedMethods?.indexOf(m.key) ?? -1) > -1}
            data-tooltip={
              (sub.unsupportedMethods?.indexOf(m.key) ?? -1) > -1 ?
                "This method is not available"
              : null
            }
            style={{ margin: 5 }}
            key={m.key}
            checked={hasSub(sub.key, m.key)}
            onClick={(_e, c) => toggleSub(sub.key, m.key, c.checked)}
          />
        ))}
      </div>
    </div>
  );
}

function NotificationSectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "bottom",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <Header style={{ display: "block", marginBottom: 0 }}>
        <h5>{title}</h5>
      </Header>
      <div
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        {possibleMethods.map((m) => (
          <Icon key={m.key} name={m.icon} style={{ margin: 5 }} />
        ))}
      </div>
    </div>
  );
}

const deletionTimeframe = [
  { key: "never", text: "Never" },
  { key: 183, text: "After 6 months of inactivity" },
  { key: 365, text: "After 12 months of inactivity" },
  { key: 730, text: "After 24 months of inactivity" },
] as const;

const ideas = constants.subscriptions.filter((s) => s.type === "ideas");
const challenges = constants.subscriptions.filter(
  (s) => s.type === "challenges",
);
const groups = constants.subscriptions.filter((s) => s.type === "groups");
const organisations = constants.subscriptions.filter(
  (s) => s.type === "organisations",
);
const general = constants.subscriptions.filter((s) => s.type === "general");

const SettingsGeneral = () => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onOrganisationEdited = useCallback<OnOrganisationEditedFn>(
    (field, value) => dispatch(actions.user.updateOrganisation(field, value)),
    [dispatch],
  );

  const ownerOrganisation = user?.ownerOrganisation as
    | SettingsOwnerOrganisation
    | undefined;
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [userIp, setUserIp] = useState("");
  const orgId = ownerOrganisation?._id;

  const { t } = useTranslation();
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  useEffect(() => {
    if (showErrorMessage) {
      window.scrollTo(0, 0);
    }
  }, [showErrorMessage]);

  const getUserIpInfo = useCallback(() => {
    if (!orgId) return;
    api.organisations.getIpAddress(
      orgId,
      ({ ip }) => {
        setUserIp(ip ?? "");
      },
      () => {},
    );
  }, [orgId]);

  const saveName = useCallback(() => {
    if (!ownerOrganisation?._id) return;
    setShowErrorMessage(false);
    api.organisations.updateSettings(
      ownerOrganisation._id,
      { name: ownerOrganisation.name },
      () => toast.success(t("generic.typeUpdated", { type: "Settings" })),
      () => setShowErrorMessage(true),
    );
  }, [t, ownerOrganisation]);

  useEffect(() => {
    getUserIpInfo();
  }, [getUserIpInfo]);

  const updateUserAccountSelfDeletion = useCallback(
    (enabled?: boolean) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { disableAccountSelfDeletion: !enabled },
        () => {
          onOrganisationEdited("disableAccountSelfDeletion", !enabled);
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
        },
        () => setShowErrorMessage(true),
      );
    },
    [t, ownerOrganisation, onOrganisationEdited],
  );

  const changeDefaultLanguage = useCallback(
    (defaultLanguage: string) => {
      if (!ownerOrganisation?._id) return;
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultLanguage },
        () => {
          onOrganisationEdited("defaultLanguage", defaultLanguage);
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
        },
        () => setShowErrorMessage(true),
      );
    },
    [t, ownerOrganisation, onOrganisationEdited],
  );

  const changeDefaultTimezone = useCallback(
    (defaultTimezone: string) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultTimezone },
        () => {
          onOrganisationEdited("defaultTimezone", defaultTimezone);
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
        },
        () => setShowErrorMessage(true),
      );
    },
    [t, ownerOrganisation, onOrganisationEdited],
  );

  const hasSub = useCallback(
    (key: string, method: SubscriptionMethod) => {
      if (!ownerOrganisation) return false;
      const subs: DefaultSubscriptionsMap = {
        ...(ownerOrganisation.defaultSubscriptions as DefaultSubscriptionsMap),
      };
      const sub = subs[key];
      return Boolean(sub && sub.indexOf(method) > -1);
    },
    [ownerOrganisation],
  );

  const toggleSub = useCallback(
    (key: string, method: SubscriptionMethod, checked?: boolean) => {
      if (!ownerOrganisation?._id) return;
      const subs: DefaultSubscriptionsMap = {
        ...(ownerOrganisation.defaultSubscriptions as DefaultSubscriptionsMap),
      };
      let methods = [...(subs[key] || [])];
      if (!methods.length && checked) methods = [method];
      if (methods.length) {
        const index = methods.indexOf(method);
        if (checked && index === -1) methods.push(method);
        if (!checked && index > -1) methods.splice(index, 1);
      }
      subs[key] = methods;
      setShowErrorMessage(false);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultSubscriptions: subs },
        () => {
          onOrganisationEdited("defaultSubscriptions", subs);
        },
        () => setShowErrorMessage(true),
      );
    },
    [ownerOrganisation, onOrganisationEdited],
  );

  const toggleNotifications = useCallback(
    (enabled?: boolean) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      if (enabled) {
        util
          .confirm(
            "Default notifications",
            "Are you sure you want to edit the default notifications of your organisation? If you do so, all new users will start with the email/push notification settings that you choose.",
          )
          .then(() => {
            api.organisations.updateSettings(
              ownerOrganisation._id,
              {
                defaultSubscriptions:
                  constants.defaultSubscriptions as OrganisationSettingsUpdate["defaultSubscriptions"],
              },
              () => {
                onOrganisationEdited(
                  "defaultSubscriptions",
                  constants.defaultSubscriptions,
                );
              },
              () => setShowErrorMessage(true),
            );
          })
          .catch(() => {});
      } else {
        api.organisations.updateSettings(
          ownerOrganisation._id,
          { defaultSubscriptions: null },
          () => {
            onOrganisationEdited("defaultSubscriptions", null);
          },
          () => setShowErrorMessage(true),
        );
      }
    },
    [ownerOrganisation, onOrganisationEdited],
  );

  const changeAutoDeletionTime = useCallback(
    (value: string | number) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      const deleteAfter = value === "never" ? null : (value as number);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { autoAccountDeletionPolicy: { deleteAfter } },
        () => {
          onOrganisationEdited("autoAccountDeletionPolicy", { deleteAfter });
        },
        () => setShowErrorMessage(true),
      );
    },
    [ownerOrganisation, onOrganisationEdited],
  );

  if (!ownerOrganisation || !user) {
    return null;
  }

  return (
    <div>
      {showErrorMessage ?
        <Message error>
          <Message.Header>There was a problem</Message.Header>
          <Message.Content>
            We were unable to save your settings. You may not have the correct
            permissions. Please contact an administrator for further advice.
          </Message.Content>
        </Message>
      : null}

      <SegmentContainer>
        <Segment>
          <h3>{t("admin.settings.general.organisation.title")}</h3>
          <p>{t("admin.settings.general.organisation.info")}</p>
          <Input
            fluid
            value={ownerOrganisation.name}
            onChange={(e) => onOrganisationEdited("name", e.target.value)}
            action={
              <Button primary content={t("generic.save")} onClick={saveName} />
            }
          />
        </Segment>
        <Segment>
          <h3>{t("admin.settings.general.language.title")}</h3>
          <p>{t("admin.settings.general.language.info", { user })}</p>
          <Select
            fluid
            value={ownerOrganisation.defaultLanguage || "en"}
            options={languages.map((l) => ({
              key: l.key,
              value: l.key,
              text: l.name,
            }))}
            onChange={(_e, s) =>
              changeDefaultLanguage(
                typeof s.value === "string" ? s.value : "en",
              )
            }
          />
          <h3>{t("admin.settings.general.timezone.title")}</h3>
          <p>{t("admin.settings.general.timezone.info", { user })}</p>
          <SelectableDropdown
            searchable
            single
            value={ownerOrganisation.defaultTimezone || DEFAULT_ORG_TIMEZONE}
            options={timezoneOptions}
            onChange={(value) => value && changeDefaultTimezone(value)}
          />
          <Message>
            Your current timezone is{" "}
            {Intl.DateTimeFormat().resolvedOptions().timeZone}.
          </Message>
        </Segment>
      </SegmentContainer>

      <SegmentContainer>
        <Segment>
          <h3>Automatically delete inactive users</h3>
          <p>
            {util.appName()} can automatically delete user accounts in{" "}
            {ownerOrganisation.name} according to a schedule that you customise.
          </p>
          <p>
            Choose an option below to schedule user accounts to be deleted after
            the selected time has elapsed since their last-seen date. Users will
            be sent warning emails a few weeks before their accounts are
            deleted.
          </p>
          <Message warning size="small">
            <strong>Please note:</strong> {t("generic.ideas")}, messages, and
            comments belonging to affected user accounts will also be deleted.
          </Message>
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <p style={{ marginRight: 10 }}>
              <strong>Delete user accounts</strong>
            </p>
            <Select
              value={
                ownerOrganisation?.autoAccountDeletionPolicy?.deleteAfter ||
                "never"
              }
              options={deletionTimeframe.map((l) => ({
                key: l.key,
                value: l.key,
                text: l.text,
              }))}
              onChange={(_e, s) =>
                changeAutoDeletionTime(
                  typeof s.value === "string" || typeof s.value === "number" ?
                    s.value
                  : "never",
                )
              }
            />
          </div>
        </Segment>

        <Segment>
          <h3>Allow users to delete their own accounts</h3>
          <p>
            By default in {util.appName()}, users can delete their own accounts.
            By disabling this, users will no longer be able to delete their own
            accounts, and only administrators will be able to delete users from{" "}
            {util.appName()}.
          </p>
          <Checkbox
            toggle
            label="Allow users to delete their own accounts"
            checked={!ownerOrganisation?.disableAccountSelfDeletion}
            onChange={(_e, c) => updateUserAccountSelfDeletion(c.checked)}
          />
        </Segment>
      </SegmentContainer>

      <SegmentContainer>
        <Segment>
          <h3>Default notification subscriptions</h3>
          <p>
            {util.appName()} will automatically assign a default set of
            notification subscriptions to new users as they join{" "}
            {ownerOrganisation.name}. If you prefer, you can configure your own
            default notification subscriptions below by choosing when users
            should receive emails and push notifications.
          </p>
          <Checkbox
            style={{ marginBottom: 10 }}
            toggle
            label="Customise event subscriptions"
            checked={!!ownerOrganisation.defaultSubscriptions}
            onChange={(_e, c) => toggleNotifications(c.checked)}
          />

          {ownerOrganisation.defaultSubscriptions && (
            <div>
              <p>
                To reset to {util.appName()}'s default notification
                subscriptions set, please deselect the toggle above.
              </p>
              <NotificationSectionHeader
                title={t("users.preferences.yourAccount.title")}
              />
              {general.map((sub) => (
                <Subscription
                  key={sub.key}
                  sub={sub}
                  hasSub={hasSub}
                  toggleSub={toggleSub}
                />
              ))}

              <Divider hidden />
              <NotificationSectionHeader title="Groups" />
              {groups.map((sub) => (
                <Subscription
                  key={sub.key}
                  sub={sub}
                  hasSub={hasSub}
                  toggleSub={toggleSub}
                />
              ))}

              <Divider hidden />
              <NotificationSectionHeader
                title={t("common:capitalise", { key: "generic.ideas" })}
              />
              {ideas.map((sub) => (
                <Subscription
                  key={sub.key}
                  sub={sub}
                  hasSub={hasSub}
                  toggleSub={toggleSub}
                />
              ))}

              <Divider hidden />
              <NotificationSectionHeader
                title={t("users.preferences.yourChallenges.title")}
              />
              {challenges.map((sub) => (
                <Subscription
                  key={sub.key}
                  sub={sub}
                  hasSub={hasSub}
                  toggleSub={toggleSub}
                />
              ))}

              <Divider hidden />
              <NotificationSectionHeader title="Organisation" />
              {organisations.map((sub) => (
                <Subscription
                  key={sub.key}
                  sub={sub}
                  hasSub={hasSub}
                  toggleSub={toggleSub}
                />
              ))}
            </div>
          )}
        </Segment>

        <IpRestriction userIp={userIp} />
      </SegmentContainer>
      <PointOfContact />
    </div>
  );
};

export default SettingsGeneral;
